import { Component } from '@verndale/core';
import { stagger, timeline } from 'motion';
import { breakpoints } from '../../helpers/variables';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import { EventBusSingleton } from '../../animation/eventBus';

class LanguageSelector extends Component {
  MOTION_ZERO_DURATION = 0.000001;
  breakpoint = window.matchMedia(
    `(min-width: ${breakpoints.tabletLandscape}px)`
  );
  lastHeight = 0;
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  constructor(el: HTMLElement) {
    super(el);
    this.init();
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      container: this.el.querySelector<HTMLElement>(
        '.language-selector__accordion-items'
      ),
      title: this.el.querySelector<HTMLElement>('.language-selector__title'),
      items: this.el.querySelectorAll<HTMLElement>(
        '.language-selector__accordion-item'
      ),
      triggers: this.el.querySelectorAll<HTMLElement>(
        '.language-selector__accordion-item-trigger'
      ),
      panels: this.el.querySelectorAll<HTMLElement>(
        '.language-selector__accordion-item-panel'
      ),
      backButtons: this.el.querySelectorAll<HTMLElement>(
        '.language-selector__back-button'
      ),
      closeButton: this.el.querySelector<HTMLElement>(
        '.language-selector__close-button'
      )
    };
  }

  addListeners() {
    [].forEach.call(this.dom.triggers, (trigger: HTMLElement) => {
      trigger.addEventListener('click', this.handleTriggerClick.bind(this));
    });
    [].forEach.call(this.dom.backButtons, (button: HTMLElement) => {
      button.addEventListener('click', this.handleBackButton.bind(this));
    });
    (this.dom.closeButton as HTMLElement).addEventListener(
      'click',
      this.closeMenu.bind(this)
    );
    document.addEventListener(
      'click',
      this.handleClickOutside.bind(this),
      true
    );
  }

  handleClickOutside(e: Event) {
    if (!this.breakpoint.matches) return;
    if (
      !(this.dom.el as HTMLElement).contains(e.target as Node) &&
      !(e.target as HTMLElement)?.classList?.contains(
        'utility-navigation__language-and-region-button'
      )
    ) {
      EventBusSingleton.publish('utilityNavigation:closingLanguageSelector');
      this.closeMenu();
    }
  }

  init() {
    [].forEach.call(this.dom.items, (item: HTMLElement) => {
      const guid = crypto.randomUUID();

      const trigger = item.querySelector(
        '.language-selector__accordion-item-trigger'
      );
      const content = item.querySelector(
        '.language-selector__accordion-item-panel'
      );

      if (!trigger || !content) return;

      if (trigger.getAttribute('aria-expanded') === 'true') {
        this.openPanel(content as HTMLElement);
      }

      trigger.setAttribute('aria-controls', `tabcontent-${guid}`);
      trigger.setAttribute('id', `tab-${guid}`);
      content.setAttribute('aria-labelledby', `tab-${guid}`);
      content.setAttribute('id', `tabcontent-${guid}`);
      content.setAttribute(
        'aria-hidden',
        trigger.getAttribute('aria-expanded') !== 'true' ? 'true' : 'false'
      );
    });
  }

  handleBackButton(e: Event) {
    const button = e.currentTarget as HTMLElement;
    if (!button) return;
    const panel = button.parentElement;
    if (!panel) return;

    if (button.classList.contains('language-selector__back-to-main')) {
      EventBusSingleton.publish('utilityNavigation:closingLanguageSelector');
      this.closeMenu();
    }
    const trigger = panel.previousElementSibling as HTMLElement;
    if (!trigger) return;
    this.closeItem(trigger);
  }

  handleTriggerClick(e: Event) {
    const button = e.currentTarget;
    if (!button) return;
    if ((button as HTMLElement).getAttribute('aria-expanded') === 'true') {
      this.closeItem(button as HTMLElement);
    } else {
      this.openItem(button as HTMLElement);
    }
  }

  openItem(trigger: HTMLElement) {
    const content = document.getElementById(
      trigger.getAttribute('aria-controls') as string
    );
    trigger.setAttribute('aria-expanded', 'true');

    if (content) this.openPanel(content);
  }

  closeItem(trigger: HTMLElement) {
    const content = document.getElementById(
      trigger.getAttribute('aria-controls') as string
    );

    trigger.setAttribute('aria-expanded', 'false');

    if (content) this.closePanel(content);
  }

  openPanel(panel: HTMLElement) {
    const { scrollHeight } = panel as HTMLElement;
    panel.style.visibility = 'visible';
    panel.setAttribute('aria-hidden', 'false');

    const sequence = [] as TimelineDefinition;
    sequence.push({ name: 'start', at: 0 });
    if (this.breakpoint.matches) {
      sequence.push([
        panel,
        { blockSize: `${scrollHeight}px` },
        {
          duration:
            this.breakpoint.matches && !this.reducedMotion.matches
              ? 0.25
              : this.MOTION_ZERO_DURATION,
          easing: 'linear',
          at: 'start'
        }
      ]);
      timeline(sequence).finished.then(() => {
        panel.style.blockSize = 'auto';
      });
    } else {
      (this.dom.container as HTMLElement).style.blockSize = `${scrollHeight}px`;
      (this.dom.container as HTMLElement).style.transform =
        'translate(-100%, 0)';
      panel.style.blockSize = 'auto';
    }
  }

  closePanel(panel: HTMLElement) {
    if (this.breakpoint.matches) {
      const sequence = [] as TimelineDefinition;
      sequence.push({ name: 'start', at: 0 });
      sequence.push({ name: 'move', at: 0.3 });
      sequence.push([
        panel,
        { blockSize: '0px' },
        {
          duration:
            this.breakpoint.matches && !this.reducedMotion
              ? 0.25
              : this.MOTION_ZERO_DURATION,
          easing: 'linear',
          at: 'start'
        }
      ]);
      timeline(sequence).finished.then(() => {
        panel.setAttribute('aria-hidden', 'true');
        panel.style.blockSize = '';
        panel.style.visibility = 'hidden';
      });
    } else {
      (this.dom.container as HTMLElement).style.transform = '';
      (this.dom.container as HTMLElement).style.blockSize = '';
      (this.dom.triggers as NodeListOf<HTMLElement>).forEach(trigger => {
        trigger.style.transform = '';
        trigger.style.opacity = '';
      });
      panel.setAttribute('aria-hidden', 'true');
      panel.style.blockSize = '';
      panel.style.visibility = 'hidden';
    }
  }

  openMenu() {
    if (this.el.classList.contains('language-selector--open')) return;
    (this.dom.el as HTMLElement).style.display = 'block';
    (this.dom.el as HTMLElement).style.visibility = 'visible';
    if (this.breakpoint.matches) {
      const sequence = [] as TimelineDefinition;
      sequence.push({ name: 'start', at: 0 });
      sequence.push({ name: 'enter', at: 0.15 });
      sequence.push([
        this.dom.el as HTMLElement,
        { transform: ['scale(1, 0)', 'scale(1, 1)'] },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.25,
          easing: 'linear',
          at: 'start'
        }
      ]);
      sequence.push([
        this.dom.title as HTMLElement,
        {
          opacity: [0, 1],
          transform: ['translate(-20px, 0)', 'translate(0, 0)']
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.5,
          at: this.reducedMotion.matches ? 'start' : 'enter'
        }
      ]);
      sequence.push([
        this.dom.closeButton as HTMLElement,
        {
          opacity: [0, 1]
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.5,
          at: this.reducedMotion.matches ? 'start' : 'enter'
        }
      ]);
      sequence.push([
        (this.dom.el as HTMLElement).querySelectorAll(
          '.language-selector__accordion-items > div'
        ) as NodeListOf<HTMLElement>,
        { clipPath: ['inset(0 100% 0 0)', 'inset(0 0 0 0)'] },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.5,
          delay: this.reducedMotion.matches ? 0 : stagger(0.1),
          at: this.reducedMotion.matches ? 'start' : 'enter'
        }
      ]);
      timeline(sequence).finished.then(() => {
        this.el.setAttribute('aria-hidden', 'false');
        this.el.classList.add('language-selector--open');
        (this.dom.closeButton as HTMLElement).removeAttribute('style');
        (this.dom.title as HTMLElement).removeAttribute('style');
        (
          (this.dom.el as HTMLElement).querySelectorAll(
            '.language-selector__accordion-items > div'
          ) as NodeListOf<HTMLElement>
        ).forEach(item => {
          item.removeAttribute('style');
        });
      });
    } else {
      (this.dom.el as HTMLElement).setAttribute('aria-hidden', 'false');
      (this.dom.el as HTMLElement).classList.add('language-selector--open');
    }
  }

  async closeMenu() {
    if (!this.el.classList.contains('language-selector--open')) return;

    if (this.breakpoint.matches) {
      const sequence = [] as TimelineDefinition;
      sequence.push({ name: 'start', at: 0 });
      sequence.push({ name: 'leave', at: 0.25 });
      sequence.push([
        (this.dom.el as HTMLElement).querySelectorAll(
          '.language-selector__accordion-items > div'
        ) as NodeListOf<HTMLElement>,
        { clipPath: ['inset(0 0 0 0)', 'inset(0 100% 0 0)'] },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.25,
          delay: this.reducedMotion.matches ? 0 : stagger(0.1),
          at: 'start'
        }
      ]);
      sequence.push([
        [this.dom.title as HTMLElement, this.dom.closeButton as HTMLElement],
        {
          opacity: [1, 0]
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.25,
          at: 'start'
        }
      ]);
      sequence.push([
        this.dom.el as HTMLElement,
        {
          transform: ['scale(1, 1)', 'scale(1, 0)']
        },
        {
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.5,
          at: this.reducedMotion.matches ? 'start' : 'leave'
        }
      ]);
      await timeline(sequence).finished.then(() => {
        (this.dom.el as HTMLElement).setAttribute('aria-hidden', 'true');
        (this.dom.el as HTMLElement).removeAttribute('style');
        (this.dom.el as HTMLElement).classList.remove(
          'language-selector--open'
        );
      });
    } else {
      (this.dom.el as HTMLElement).setAttribute('aria-hidden', 'false');
      (this.dom.el as HTMLElement).removeAttribute('style');
      (this.dom.el as HTMLElement).classList.remove('language-selector--open');
    }
  }
}

export default LanguageSelector;
